/**
 * Color Swatch Hover Image Switch
 *
 * Switch product image when hovering over color swatch options
 * Directly replaces the main image (image--one) for immediate effect
 * Also updates links and buttons to use the selected variant
 *
 * Debug mode: Set window.COLOR_SWATCH_DEBUG = true to enable debug logs
 */
window.COLOR_SWATCH_DEBUG = window.COLOR_SWATCH_DEBUG || false;

function debugLog(...args) {
  if (window.COLOR_SWATCH_DEBUG) {
    console.log('[Color Swatch Hover]', ...args);
  }
}

const COLOR_SWATCH_STATE = {
  initialized: false,
  // Store original data for each product item
  originalData: new Map()
};

function findMainImageContainer(productItem) {
  return productItem.querySelector('.product-item__image--one');
}

function findMainImageImg(productItem) {
  const container = findMainImageContainer(productItem);
  return container?.querySelector('img');
}

function findImageLink(productItem) {
  return productItem.querySelector('.product-item__image-link');
}

function findTitleLink(productItem) {
  return productItem.querySelector('.product-item__product-title a');
}

function findQuickAddButton(productItem) {
  return productItem.querySelector('.product-item__quick-add-button');
}

function findVariantSelector(productItem) {
  return productItem.querySelector('#variant-selector');
}

function storeOriginalData(productItem) {
  if (COLOR_SWATCH_STATE.originalData.has(productItem)) {
    return;
  }

  const imageLink = findImageLink(productItem);
  const titleLink = findTitleLink(productItem);
  const quickAddButton = findQuickAddButton(productItem);
  const variantSelector = findVariantSelector(productItem);
  const mainImage = findMainImageImg(productItem);

  // Store complete original data
  const originalData = {
    // Image data
    image: {
      src: mainImage?.src || '',
      srcset: mainImage?.srcset || '',
      className: mainImage?.className || '',
      alt: mainImage?.alt || '',
      dataSrc: mainImage?.getAttribute('data-src') || '',
      dataSrcset: mainImage?.getAttribute('data-srcset') || '',
      dataWidths: mainImage?.getAttribute('data-widths') || '',
      dataAspectratio: mainImage?.getAttribute('data-aspectratio') || '',
      dataSizes: mainImage?.getAttribute('data-sizes') || ''
    },
    // Link data
    imageLink: {
      href: imageLink?.href || '',
      tagName: imageLink?.tagName || ''
    },
    titleLink: {
      href: titleLink?.href || ''
    },
    // Quick add button data
    quickAddButton: {
      productId: quickAddButton?.getAttribute('data-product-id') || '',
      quickAddHandle: quickAddButton?.getAttribute('data-quick-add') || ''
    },
    // Variant selector data
    variantSelector: {
      selectedValue: variantSelector?.value || ''
    }
  };

  COLOR_SWATCH_STATE.originalData.set(productItem, originalData);
  debugLog('Stored original data for product item');
}

function getOriginalData(productItem) {
  return COLOR_SWATCH_STATE.originalData.get(productItem);
}

function normalizeVariantImageUrl(variantImageUrl) {
  if (!variantImageUrl) return '';

  const trimmedUrl = variantImageUrl.trim();

  if (trimmedUrl.startsWith('//')) {
    return `https:${trimmedUrl}`;
  }

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  const relativeUrl = trimmedUrl.replace(/^\/+/, '');

  if (relativeUrl.startsWith('cdn/shop/')) {
    return `${window.location.origin}/${relativeUrl}`;
  }

  if (relativeUrl.startsWith('files/') || relativeUrl.startsWith('products/')) {
    return `${window.location.origin}/cdn/shop/${relativeUrl}`;
  }

  return `${window.location.origin}/${relativeUrl}`;
}

function generateSrcsetFromUrl(baseUrl) {
  if (!baseUrl) return '';

  const [variantUrlWithoutParams, queryString] = baseUrl.split('?');
  const extensionMatch = variantUrlWithoutParams.match(/\.([a-zA-Z0-9]+)$/);

  if (!extensionMatch) return '';

  const extension = extensionMatch[1];
  const queryParams = queryString ? `?${queryString}` : '';
  const variantUrlBase = variantUrlWithoutParams
    .replace(/\.[^/.]+$/, '')
    .replace(/_(\d+x\d*|\d*x\d+)$/, '');

  return `${variantUrlBase}_500x.${extension}${queryParams} 500w, ${variantUrlBase}_800x.${extension}${queryParams} 800w, ${variantUrlBase}_1200x.${extension}${queryParams} 1200w`;
}

function switchToVariantImage(productItem, variantImageUrl) {
  const img = findMainImageImg(productItem);
  if (!img) {
    debugLog('No main image found');
    return;
  }

  const variantUrlHttps = normalizeVariantImageUrl(variantImageUrl);
  const variantSrcset = generateSrcsetFromUrl(variantUrlHttps);
  
  img.src = variantUrlHttps;
  img.srcset = variantSrcset;
  
  img.removeAttribute('data-src');
  img.removeAttribute('data-srcset');
  img.removeAttribute('data-widths');
  img.removeAttribute('data-aspectratio');
  img.removeAttribute('data-sizes');
  
  img.classList.remove('lazyautosizes', 'lazyload', 'lazyloading');
  img.classList.add('lazyloaded');
  
  debugLog('Switched main image to variant:', variantUrlHttps);
  debugLog('Generated srcset:', variantSrcset);
}

function updateProductLinks(productItem, variantId) {
  const imageLink = findImageLink(productItem);
  const titleLink = findTitleLink(productItem);
  const originalData = getOriginalData(productItem);
  
  if (!originalData) return;
  
  // Update image link if it's an anchor tag
  if (imageLink && imageLink.tagName === 'A') {
    const originalHref = originalData.imageLink.href;
    const newHref = `${originalHref}?variant=${variantId}`;
    imageLink.href = newHref;
    debugLog('Updated image link to:', newHref);
  }
  
  // Update title link
  if (titleLink) {
    const originalHref = originalData.titleLink.href;
    const newHref = `${originalHref}?variant=${variantId}`;
    titleLink.href = newHref;
    debugLog('Updated title link to:', newHref);
  }
  
  // Update quick add button if it exists
  const quickAddButton = findQuickAddButton(productItem);
  if (quickAddButton) {
    quickAddButton.setAttribute('data-product-id', variantId);
    debugLog('Updated quick add button product ID to:', variantId);
  }
  
  // Update variant selector if it exists
  const variantSelector = findVariantSelector(productItem);
  if (variantSelector) {
    variantSelector.value = variantId;
    debugLog('Updated variant selector to:', variantId);
  }
}

function restoreOriginalImage(productItem) {
  const img = findMainImageImg(productItem);
  const originalData = getOriginalData(productItem);
  
  if (!img || !originalData) {
    debugLog('No main image or original data found');
    return;
  }
  
  const originalImageData = originalData.image;
  
  // Restore all original image attributes
  img.src = originalImageData.src;
  img.srcset = originalImageData.srcset;
  img.className = originalImageData.className;
  img.alt = originalImageData.alt;
  
  // Restore data attributes
  if (originalImageData.dataSrc) img.setAttribute('data-src', originalImageData.dataSrc);
  if (originalImageData.dataSrcset) img.setAttribute('data-srcset', originalImageData.dataSrcset);
  if (originalImageData.dataWidths) img.setAttribute('data-widths', originalImageData.dataWidths);
  if (originalImageData.dataAspectratio) img.setAttribute('data-aspectratio', originalImageData.dataAspectratio);
  if (originalImageData.dataSizes) img.setAttribute('data-sizes', originalImageData.dataSizes);
  
  // Restore lazyload classes if needed
  if (originalImageData.className.includes('lazyload')) {
    img.classList.add('lazyload');
    img.classList.remove('lazyloaded');
  }
  
  debugLog('Restored original main image');
}

function restoreProductLinks(productItem) {
  const imageLink = findImageLink(productItem);
  const titleLink = findTitleLink(productItem);
  const quickAddButton = findQuickAddButton(productItem);
  const variantSelector = findVariantSelector(productItem);
  const originalData = getOriginalData(productItem);
  
  if (!originalData) return;
  
  // Restore image link if it's an anchor tag
  if (imageLink && imageLink.tagName === 'A') {
    imageLink.href = originalData.imageLink.href;
    debugLog('Restored image link to:', originalData.imageLink.href);
  }
  
  // Restore title link
  if (titleLink) {
    titleLink.href = originalData.titleLink.href;
    debugLog('Restored title link to:', originalData.titleLink.href);
  }
  
  // Restore quick add button
  if (quickAddButton) {
    quickAddButton.setAttribute('data-product-id', originalData.quickAddButton.productId);
    debugLog('Restored quick add button product ID to:', originalData.quickAddButton.productId);
  }
  
  // Restore variant selector
  if (variantSelector) {
    variantSelector.value = originalData.variantSelector.selectedValue;
    debugLog('Restored variant selector to:', originalData.variantSelector.selectedValue);
  }
}

function switchToVariant(productItem, variantImageUrl, variantId) {
  // Switch the image first
  switchToVariantImage(productItem, variantImageUrl);
  
  // Then update all links and buttons
  updateProductLinks(productItem, variantId);
}

function restoreOriginalProduct(productItem) {
  // Restore the image first
  restoreOriginalImage(productItem);
  
  // Then restore all links and buttons
  restoreProductLinks(productItem);
}

function initColorSwatchHover() {
  if (COLOR_SWATCH_STATE.initialized) {
    debugLog('Already initialized');
    return;
  }

  debugLog('Initializing color swatch hover...');
  
  // Event listeners for each product item
  const productItems = document.querySelectorAll('.product-item');
  productItems.forEach(productItem => {
    // Store original data for this product
    storeOriginalData(productItem);
    
    // Get color swatch items for this product
    const swatchItems = productItem.querySelectorAll('.product-item-options__item[data-variant-image]');
    
    // Add mouseover listeners to swatch items
    swatchItems.forEach(swatchItem => {
      swatchItem.addEventListener('mouseover', (e) => {
        // Get variant data from either the swatch item or its child button
        const variantImageUrl = swatchItem.dataset.variantImage;
        const variantId = swatchItem.dataset.variantId;
        
        if (variantImageUrl && variantId) {
          debugLog('Hovering color swatch, variant:', variantId, 'image:', variantImageUrl);
          switchToVariant(productItem, variantImageUrl, variantId);
        }
      });
    });
    
    // Add mouseleave listener to the product item itself
    productItem.addEventListener('mouseleave', (e) => {
      // Always restore original product when mouse leaves
      debugLog('Mouse left product item, restoring original state');
      restoreOriginalProduct(productItem);
    });
  });

  COLOR_SWATCH_STATE.initialized = true;
  debugLog('Initialization complete');
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initColorSwatchHover);
} else {
  initColorSwatchHover();
}

// Expose functions for testing
document.initColorSwatchHover = initColorSwatchHover;
document.restoreOriginalProduct = restoreOriginalProduct;
document.switchToVariant = switchToVariant;
document.colorSwatchDebug = (enable) => {
  window.COLOR_SWATCH_DEBUG = enable;
  debugLog('Debug mode:', enable ? 'enabled' : 'disabled');
};
